import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

export default function LanguageSwitcher({
  tone = 'light',
}: {
  tone?: 'light' | 'dark';
}) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  const buttonClass =
    tone === 'light'
      ? 'text-foreground-700 hover:text-foreground-950 border-background-300 hover:border-background-400'
      : 'text-background-100 hover:text-background-50 border-background-100/40 hover:border-background-50';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-full border bg-transparent px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${buttonClass}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-global-line text-base leading-none"></i>
        </span>
        <span className="text-sm">{current.label}</span>
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-down-s-line text-base leading-none"></i>
        </span>
      </button>

      {open && (
        <ul
          className="absolute right-0 z-50 mt-2 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1 shadow-none"
          role="listbox"
        >
          {LANGUAGES.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                role="option"
                aria-selected={l.code === i18n.language}
                onClick={() => {
                  i18n.changeLanguage(l.code);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  l.code === i18n.language
                    ? 'bg-primary-100 font-semibold text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <span>{l.label}</span>
                {l.code === i18n.language && (
                  <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                    <i className="ri-check-line text-base leading-none"></i>
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}